import React, { useState, useRef, useEffect } from "react";
import type { ConversationMeta } from "./types";
import { formatRelativeTime, truncate } from "./helpers";
import { loadConversationList, deleteConversation, renameConversation } from "./persistence";

interface ConversationSidebarProps {
  currentSessionId: string;
  onSelectConversation: (sessionId: string) => void;
  onNewChat: () => void;
  trigger: React.ReactNode;
}

export default function ConversationSidebar({
  currentSessionId,
  onSelectConversation,
  onNewChat,
  trigger,
}: ConversationSidebarProps) {
  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState<ConversationMeta[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setConversations(loadConversationList());
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleRename = (id: string) => {
    if (editValue.trim()) {
      renameConversation(id, editValue.trim());
      setConversations(loadConversationList());
    }
    setEditingId(null);
  };

  const handleDelete = (id: string) => {
    deleteConversation(id);
    setConversations(loadConversationList());
    setConfirmDeleteId(null);
  };

  const filtered = search
    ? conversations.filter(
        (c) =>
          c.title.toLowerCase().includes(search.toLowerCase()) ||
          c.lastMessage.toLowerCase().includes(search.toLowerCase()),
      )
    : conversations;

  return (
    <div className="relative" ref={panelRef}>
      <div onClick={() => setOpen(!open)}>{trigger}</div>

      {open && (
        <div
          className="absolute bottom-full left-0 mb-2 w-72 rounded-xl shadow-lg z-50 overflow-hidden"
          style={{
            backgroundColor: "var(--c-surface)",
            border: "1px solid var(--c-border)",
          }}
        >
          {/* Header */}
          <div className="px-3 pt-3 pb-2 flex items-center justify-between">
            <span className="text-xs font-medium" style={{ color: "var(--c-text)" }}>
              Conversations
            </span>
            <button
              onClick={() => {
                onNewChat();
                setOpen(false);
              }}
              className="flex items-center gap-1 px-2 py-1 rounded text-[11px] transition-colors"
              style={{ color: "var(--c-primary)" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "var(--c-primary-subtle)")
              }
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M6 2v8M2 6h8" />
              </svg>
              New
            </button>
          </div>

          {/* Search */}
          {conversations.length > 3 && (
            <div className="px-3 pb-2">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search conversations..."
                className="w-full px-2 py-1.5 text-[11px] rounded-md focus:outline-none"
                style={{
                  backgroundColor: "var(--c-surface-2)",
                  color: "var(--c-text)",
                  border: "1px solid var(--c-border)",
                }}
              />
            </div>
          )}

          {/* List */}
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <div
                className="px-3 py-4 text-center text-[11px]"
                style={{ color: "var(--c-text-faint)" }}
              >
                {search ? "No matching conversations" : "No conversations yet"}
              </div>
            ) : (
              filtered.map((conv) => (
                <div
                  key={conv.id}
                  className="group px-3 py-2 transition-colors cursor-pointer"
                  style={{
                    backgroundColor:
                      conv.id === currentSessionId ? "var(--c-primary-subtle)" : undefined,
                  }}
                  onMouseEnter={(e) => {
                    if (conv.id !== currentSessionId)
                      e.currentTarget.style.backgroundColor = "var(--c-hover)";
                  }}
                  onMouseLeave={(e) => {
                    if (conv.id !== currentSessionId) e.currentTarget.style.backgroundColor = "";
                  }}
                  onClick={() => {
                    if (editingId || confirmDeleteId) return;
                    onSelectConversation(conv.id);
                    setOpen(false);
                  }}
                >
                  {editingId === conv.id ? (
                    <input
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleRename(conv.id);
                        if (e.key === "Escape") setEditingId(null);
                      }}
                      onBlur={() => handleRename(conv.id)}
                      autoFocus
                      className="w-full px-1 py-0.5 text-xs rounded focus:outline-none"
                      style={{
                        backgroundColor: "var(--c-surface-2)",
                        color: "var(--c-text)",
                        border: "1px solid var(--c-primary)",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : confirmDeleteId === conv.id ? (
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <span className="text-[11px]" style={{ color: "var(--c-danger)" }}>
                        Delete?
                      </span>
                      <button
                        onClick={() => handleDelete(conv.id)}
                        className="px-2 py-0.5 rounded text-[10px]"
                        style={{ backgroundColor: "var(--c-danger)", color: "#fff" }}
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="px-2 py-0.5 rounded text-[10px]"
                        style={{ color: "var(--c-text-secondary)" }}
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <div
                          className="text-xs font-medium truncate"
                          style={{ color: "var(--c-text)" }}
                        >
                          {conv.title}
                        </div>
                        <div
                          className="text-[10px] truncate mt-0.5"
                          style={{ color: "var(--c-text-faint)" }}
                        >
                          {truncate(conv.lastMessage, 50)}
                        </div>
                        <div className="text-[9px] mt-0.5" style={{ color: "var(--c-text-faint)" }}>
                          {formatRelativeTime(conv.timestamp)} · {conv.messageCount} msgs
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ml-2 shrink-0">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingId(conv.id);
                            setEditValue(conv.title);
                          }}
                          className="p-0.5 rounded"
                          style={{ color: "var(--c-text-faint)" }}
                          title="Rename"
                        >
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 11 11"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                          >
                            <path d="M7 1.5l2.5 2.5-6 6H1v-2.5l6-6z" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDeleteId(conv.id);
                          }}
                          className="p-0.5 rounded"
                          style={{ color: "var(--c-text-faint)" }}
                          title="Delete"
                        >
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 11 11"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                          >
                            <path d="M2 3h7M4 3V2h3v1M3 3v6a1 1 0 001 1h3a1 1 0 001-1V3" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
